/**
 * EN-1862: detects that the running web app is not the latest deployed build.
 *
 * The bundle carries a build id (VUE_APP_BUILD_ID, stamped by vue.config.js)
 * and every deploy writes dist/version.json with the same id. After login the
 * app fetches version.json fresh (on a timer, on tab focus, and at login) and
 * compares. A difference means the user is on an old, cached build. The app
 * only offers a Refresh; it never reloads by itself, so a live pig run view or
 * a half-filled form is never lost.
 *
 * Without a build id (dev server) nothing here does anything or errors.
 */

export const VERSION_CHECK_INTERVAL_MS = 10 * 60 * 1000;
/** Focus and tab events closer together than this reuse the last check. */
export const VERSION_CHECK_MIN_GAP_MS = 60 * 1000;

export type VersionCheckReason = 'login' | 'timer' | 'focus';

export function versionUrl(): string {
  return `${process.env.BASE_URL || '/'}version.json`;
}

/** The build id stamped into this bundle; empty when it was not stamped. */
export function runningBuildId(): string {
  return (process.env.VUE_APP_BUILD_ID || '').trim();
}

/** The build id in a version.json body, or null for anything else. */
export function parseVersionJson(text: string): string | null {
  try {
    const parsed = JSON.parse(text);
    if (parsed === null || typeof parsed !== 'object') return null;
    const id = (parsed as { buildId?: unknown }).buildId;
    if (typeof id !== 'string') return null;
    const trimmed = id.trim();
    return trimmed || null;
  } catch {
    return null;
  }
}

/** True only when both ids are known and differ. */
export function isNewVersionAvailable(
  running: string,
  remote: string | null | undefined
): boolean {
  if (!running || !remote) return false;
  return running !== remote;
}

export interface VersionFetchResponse {
  ok: boolean;
  text(): Promise<string>;
}

export type VersionFetch = (
  input: string,
  init?: { cache?: RequestCache; headers?: Record<string, string> }
) => Promise<VersionFetchResponse>;

/**
 * Fetches version.json fresh and returns its build id. Never rejects: a
 * missing file (404), an HTML fallback page, a network error or a bad body
 * all give null, quietly.
 */
export async function fetchLatestBuildId(
  fetchImpl: VersionFetch,
  now: number = Date.now()
): Promise<string | null> {
  try {
    const response = await fetchImpl(`${versionUrl()}?t=${now}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!response.ok) return null;
    return parseVersionJson(await response.text());
  } catch {
    return null;
  }
}

interface EventSource {
  addEventListener(type: string, fn: () => void): void;
  removeEventListener(type: string, fn: () => void): void;
}

export interface VersionWatcherOptions {
  runningBuildId: string;
  fetchImpl: VersionFetch;
  onCheck: (available: boolean, reason: VersionCheckReason) => void;
  intervalMs?: number;
  minGapMs?: number;
  now?: () => number;
  doc?: EventSource & { visibilityState: string };
  win?: EventSource;
  setIntervalFn?: (fn: () => void, ms: number) => unknown;
  clearIntervalFn?: (handle: unknown) => void;
}

/**
 * Runs the check at login (start), every ten minutes, and when the tab or
 * window comes back into focus. A failed check keeps the last answer.
 */
export class VersionWatcher {
  updateAvailable = false;

  private readonly runningId: string;
  private readonly fetchImpl: VersionFetch;
  private readonly onCheck: VersionWatcherOptions['onCheck'];
  private readonly intervalMs: number;
  private readonly minGapMs: number;
  private readonly now: () => number;
  private readonly doc?: VersionWatcherOptions['doc'];
  private readonly win?: VersionWatcherOptions['win'];
  private readonly setIntervalFn: (fn: () => void, ms: number) => unknown;
  private readonly clearIntervalFn: (handle: unknown) => void;

  private timer: unknown = null;
  private started = false;
  private inFlight = false;
  private lastCheckAt: number | null = null;

  constructor(options: VersionWatcherOptions) {
    this.runningId = options.runningBuildId;
    this.fetchImpl = options.fetchImpl;
    this.onCheck = options.onCheck;
    this.intervalMs = options.intervalMs ?? VERSION_CHECK_INTERVAL_MS;
    this.minGapMs = options.minGapMs ?? VERSION_CHECK_MIN_GAP_MS;
    this.now = options.now ?? Date.now;
    this.doc =
      options.doc ?? (typeof document !== 'undefined' ? document : undefined);
    this.win =
      options.win ?? (typeof window !== 'undefined' ? window : undefined);
    this.setIntervalFn =
      options.setIntervalFn ?? ((fn, ms) => setInterval(fn, ms));
    this.clearIntervalFn =
      options.clearIntervalFn ??
      ((handle) => clearInterval(handle as ReturnType<typeof setInterval>));
  }

  private readonly onVisibility = () => {
    if (this.doc?.visibilityState === 'visible') void this.check('focus');
  };

  private readonly onFocus = () => {
    void this.check('focus');
  };

  start(): void {
    // No build id (dev server): nothing to compare, so do nothing at all.
    if (this.started || !this.runningId) return;
    this.started = true;
    this.doc?.addEventListener('visibilitychange', this.onVisibility);
    this.win?.addEventListener('focus', this.onFocus);
    this.timer = this.setIntervalFn(() => {
      void this.check('timer');
    }, this.intervalMs);
    void this.check('login');
  }

  stop(): void {
    this.started = false;
    this.doc?.removeEventListener('visibilitychange', this.onVisibility);
    this.win?.removeEventListener('focus', this.onFocus);
    if (this.timer !== null) {
      this.clearIntervalFn(this.timer);
      this.timer = null;
    }
  }

  async check(reason: VersionCheckReason): Promise<void> {
    if (!this.started || this.inFlight) return;
    // A hidden tab does not poll; coming back to it runs a focus check.
    if (reason === 'timer' && this.doc?.visibilityState === 'hidden') return;
    const at = this.now();
    if (
      reason === 'focus' &&
      this.lastCheckAt !== null &&
      at - this.lastCheckAt < this.minGapMs
    ) {
      return;
    }
    this.inFlight = true;
    this.lastCheckAt = at;
    try {
      const latest = await fetchLatestBuildId(this.fetchImpl, at);
      // A failed check (null) keeps the last answer.
      if (latest === null || !this.started) return;
      this.updateAvailable = isNewVersionAvailable(this.runningId, latest);
      try {
        this.onCheck(this.updateAvailable, reason);
      } catch (error) {
        console.error('Version check callback failed:', error);
      }
    } finally {
      this.inFlight = false;
    }
  }
}

/** What the banner shows: visible while an update is available and not dismissed. */
export class NewVersionNotice {
  available = false;
  dismissed = false;

  get visible(): boolean {
    return this.available && !this.dismissed;
  }

  /** Called after every check; a check that still finds an update re-shows it. */
  onCheck(available: boolean): void {
    this.available = available;
    if (available) this.dismissed = false;
  }

  dismiss(): void {
    this.dismissed = true;
  }

  refresh(reload: () => void): void {
    reload();
  }
}
